import React, { useEffect, useMemo, useState } from 'react';

import { MCButton, Spinner, View } from 'src/components/basic';
import { Categories } from 'src/components/doctor/Categories';
import LANG_CONST from 'src/helpers/lang/const';
import { api, scssVariables as variables, userFullName, useScreenDetail } from 'src/helpers';
import Message from './Message';
import { DoctorDetail, DoctorHeader } from './Doctor';
import doctorsModel from '../doctorsModel';
import { Doctors } from './Doctors';
import { MAX_WIDTH, MIN_DESKTOP_WIDTH, remCss, remFontCss } from '../utils';
import doctorModel from '../doctorModel';
import { DoctorRight } from 'src/components/doctor/DoctorDetail';

const Content = ({
  doctor,
  clinic,
  isConv,
  onSendMsg,
  onBook,
  locations,
  onBack,
  selectedDate,
  slotsMonth
}) => {
  const [selectedHosMap, setSelectedHosMap] = useState(() => locations[0] || null);
  const screen = useScreenDetail();
  const medium = MIN_DESKTOP_WIDTH > screen.innerWidth;
  const activeSelectedHosMap =
    locations.find((item) => item.id === selectedHosMap?.id) || locations[0] || null;

  return (
    <View
      data-testid={'public-doctor-profile'}
      data-doctor-id={doctor?.id || ''}
      style={{ gap: remCss(25), height: '100%', overflow: 'auto' }}
    >
      {!isConv && (
        <DoctorHeader
          doctor={doctor}
          onSendMsg={() => {
            onSendMsg(doctor);
          }}
          onBook={() => {
            onBook(doctor);
          }}
        />
      )}
      <View
        style={{
          flexDirection: medium ? 'column' : 'row',
          flex: 1,
          height: '100%',
          overflow: 'auto'
        }}
      >
        {isConv ? (
          <Message contact={doctor} endConversation={onBack} />
        ) : (
          <DoctorDetail
            doctor={doctor}
            medium={medium}
            hospitalName={clinic ? userFullName(clinic) : doctor.hospitalName?.split('_')[0]}
            locations={locations}
            selectedHosMap={activeSelectedHosMap}
            setSelectedHosMap={setSelectedHosMap}
          />
        )}
        {(!isConv || !medium) && (
          <View
            style={{
              paddingLeft: remCss(30),
              paddingTop: remCss(30),
              ...(!medium && {
                borderLeftColor: variables['colors-mc-border2'],
                borderLeftWidth: 2,
                borderLeftStyle: 'solid',
                height: 'fit-content',
                minHeight: '100%',
                paddingTop: 0
              }),
              ...(isConv && {
                height: '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
                paddingTop: 0
              })
            }}
          >
            {isConv && (
              <div style={{ marginBottom: remCss(40), marginLeft: 'auto' }}>
                <MCButton
                  label={LANG_CONST.buttons.make_appointment}
                  primary
                  largeMedium
                  dark
                  radius
                  onClick={() => onBook(doctor)}
                />
              </div>
            )}
            <DoctorRight
              doctor={doctor}
              selectedHosMap={activeSelectedHosMap}
              selectedMonth={doctorModel.selectedMonth}
              selectedDay={doctorModel.selectedDay}
              selectedDate={selectedDate}
              slotsMonth={slotsMonth}
              onBookAppointment={() => onBook(doctor)}
            />
          </View>
        )}
      </View>
    </View>
  );
};

export const DoctorsTab = ({
  clinic,
  isConv,
  onSendMsg,
  onBook,
  onDoctorItem,
  medium,
  onBack,
  contentStyle
}) => {
  doctorModel.use();
  doctorsModel.use();

  const [specialist, setSpecialist] = useState(clinic.doctorsSpecialist || []);
  const [specialistLoadedKey, setSpecialistLoadedKey] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);

  const screen = useScreenDetail();
  const defaultSpecialist = useMemo(
    () => clinic.doctorsSpecialist || [],
    [clinic.doctorsSpecialist]
  );
  const shouldFetchDoctors = defaultSpecialist.length < 4 && !!doctorsModel.listParams(false);
  const specialistKey = `${clinic.id || ''}_${defaultSpecialist
    .map((item) => (typeof item === 'string' ? item : item.id || item.label || ''))
    .join('|')}`;
  const specialistList =
    shouldFetchDoctors && specialistLoadedKey !== specialistKey ? defaultSpecialist : specialist;
  const showCategories = !shouldFetchDoctors || specialistLoadedKey === specialistKey;

  useEffect(() => {
    if (!shouldFetchDoctors) return undefined;

    let ignore = false;
    const req = api(doctorsModel.apiConfig.list.url, {
      ...doctorsModel.listParams(false),
      limit: 100
    }).then(({ error, data }) => {
      if (ignore || error === 'cancel') return;
      setSpecialistLoadedKey(specialistKey);

      if (error) return;

      setSpecialist([
        ...defaultSpecialist,
        ...(data?.items || []).map((u) => ({ ...u, label: userFullName(u) }))
      ]);
    });

    return () => {
      ignore = true;
      if (req?.cancel) req?.cancel();
    };
  }, [defaultSpecialist, shouldFetchDoctors, specialistKey]);

  const doctor = doctorModel.state.doctor || {};

  return (
    <>
      <View
        style={{
          marginHorizontal: remCss(50),
          ...(screen.innerWidth > MAX_WIDTH + remCss(100) && {
            width: MAX_WIDTH,
            marginHorizontal: 'auto'
          }),
          marginBottom: remCss(10)
        }}
      >
        {showCategories && (
          <Categories
            remCss={remCss}
            remFontCss={remFontCss}
            specialist={specialistList}
            onClick={(value) => {
              const selected = value === selectedCategory ? null : value;
              if (typeof selected === 'string') {
                doctorsModel.onChangeSpecialist(
                  doctorsModel.state.specialist === selected ? null : selected
                );
              } else if (selected) {
                doctorsModel.onChangeSpecialist('');
                onDoctorItem(selected);
              } else {
                doctorsModel.onChangeSpecialist('');
              }
              setSelectedCategory(selected);
            }}
            selected={selectedCategory ? [selectedCategory] : null}
          />
        )}
      </View>
      <View
        style={{
          ...contentStyle,
          marginTop: remCss(10),
          flexDirection: 'row'
        }}
      >
        <Doctors onDoctorItem={onDoctorItem} />
        <View
          style={{
            flex: 1,
            borderLeftColor: variables['colors-mc-border2'],
            borderLeftWidth: 2,
            borderLeftStyle: 'solid',
            height: screen.innerHeight - remCss(150)
          }}
        >
          {doctorModel.getApiState('doctor_detail').loading ? (
            <View style={{ margin: 'auto' }}>
              <Spinner />
            </View>
          ) : (
            !!doctor?.id && (
              <Content
                onBack={onBack}
                isConv={isConv}
                onSendMsg={onSendMsg}
                onBook={onBook}
                doctor={doctor}
                clinic={doctorModel.state.clinic}
                locations={doctorModel.state.hospitals}
                selectedDate={doctorModel.state.selectedDate}
                slotsMonth={doctorModel.state.slotsMonth}
                medium={medium}
              />
            )
          )}
        </View>
      </View>
    </>
  );
};
